import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router";
import { RecordAuditInfo } from "./RecordAuditInfo";
import { appendUnique, firstPage, listedRows, nextPage, trimPage } from "../../lib/paging";
import { LoadMore } from "./LoadMore";
import { Card, Button, Cluster, Drawer, Input, Select, Stack, EmptyState, useToast } from "@salesapp/ui";
import { crmApi, type DuplicateMatch, type Product } from "../../lib/crmApi";
import { reportSaveBlocked, reportSaveFailure } from "../../lib/saveNotice";
import { ACTION_HINTS, FIELD_HINTS, PRODUCT_STATUS_OPTIONS } from "../../help/fieldHints";
import { opsApi, type FieldDef, type Territory } from "../../lib/opsApi";
import { useAccess } from "../../shell/AccessProvider";
import { useLocale } from "../../shell/LocaleProvider";
import { CURRENCY_SYMBOLS } from "../../lib/localeFormat";
import { ListWorkspace, DeleteRecordButton, DetailSection, FieldItem } from "./RecordWorkspace";
import { AskLoxleyButton } from "../../loxley/AskLoxleyButton";
import { useLoxleyOffer } from "../../loxley/LoxleyProvider";
import { FieldLabel } from "./FieldLabel";
import { optionsList, SearchBar, StatusSlicer, statusLabel } from "./SearchBar";
import { StatusBadge } from "./StatusBadge";
import { ResponsiveDataTable } from "./ResponsiveDataTable";
import { TabFieldOptionsSettings } from "./TabFieldOptionsSettings";

const DEFAULT_STATUSES = ["active", "draft", "inactive"];

function emptyProductForm() {
  return {
    name: "",
    sku: "",
    description: "",
    unit_price: "",
    currency: "",
    unit_of_measure: "each",
    product_category: "",
    product_status: "active",
    territory_id: "",
    custom_fields: {} as Record<string, string>,
  };
}

export function ProductsPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { currency: localeCurrency, formatMoney } = useLocale();
  const { can } = useAccess();
  const canAssignTerritory = can("crm.territories");
  const canConfigureOptions = can("crm.field_options", "configure");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [fields, setFields] = useState<FieldDef[]>([]);
  const [territories, setTerritories] = useState<Territory[]>([]);
  const [uoms, setUoms] = useState<string[]>(["each"]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(() => searchParams.get("q") ?? "");
  const [statusFilter, setStatusFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = products.find((item) => item.id === viewingId) ?? null;
  useLoxleyOffer(viewing ? { kind: "product", id: viewing.id, label: viewing.name } : null);
  const [duplicates, setDuplicates] = useState<DuplicateMatch | null>(null);
  const [formData, setFormData] = useState(emptyProductForm);

  const statusField = fields.find((field) => field.field_key === "product_status");
  const statuses = optionsList(statusField?.options, DEFAULT_STATUSES);
  const flexFields = fields.filter((field) => !field.native && field.visible && field.field_type !== "json");
  const isFieldVisible = (fieldKey: string) => {
    const field = fields.find((item) => item.field_key === fieldKey);
    return field ? field.visible : true;
  };

  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const listed = listedRows(await crmApi.listProducts(orgId, search || undefined, nextPage(products.length), { withTotal: true }));
      const page = trimPage(listed.rows);
      setProducts((current) => appendUnique(current, page.items));
      setHasMore(page.hasMore);
      setTotal(listed.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more products");
    } finally {
      setLoadingMore(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, catalogue, territoryRows, uomRows, categoryRows] = await Promise.all([
        crmApi.listProducts(orgId, search || undefined, firstPage(), { withTotal: true }),
        opsApi.listFields(orgId, "product").catch(() => ({ fields: [] as FieldDef[] })),
        opsApi.listTerritories(orgId).then((row) => row.territories).catch(() => [] as Territory[]),
        crmApi.getRegistry("unit_of_measure", orgId).catch(() => []),
        crmApi.getRegistry("product_category", orgId).catch(() => []),
      ]);
      const listed = listedRows(data);
      const page = trimPage(listed.rows);
      setProducts(page.items);
      setHasMore(page.hasMore);
      setTotal(listed.total);
      setFields(catalogue.fields);
      setTerritories(territoryRows);
      setUoms(uomRows.length ? uomRows.map((row) => row.value) : ["each"]);
      setCategories(categoryRows.map((row) => row.value));
      setFormData((current) => {
        if (current.territory_id) return current;
        const globalRow = territoryRows.find((item) => item.code === "global") ?? territoryRows[0];
        return globalRow ? { ...current, territory_id: globalRow.id } : current;
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load products");
    } finally {
      setLoading(false);
    }
  }, [orgId, search]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!formData.name.trim() && !formData.sku.trim()) {
      setDuplicates(null);
      return;
    }
    const handle = window.setTimeout(() => {
      void crmApi
        .findProductDuplicates(orgId, { sku: formData.sku || undefined, name: formData.name || undefined })
        .then(setDuplicates)
        .catch(() => setDuplicates(null));
    }, 300);
    return () => window.clearTimeout(handle);
  }, [orgId, formData.name, formData.sku]);

  // Clicking a product opens it to read; editing is a deliberate click on Edit.
  const startView = (product: Product) => {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyProductForm());
    setViewingId(product.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const startEdit = (product: Product) => {
    setViewingId(null);
    setEditingId(product.id);
    setShowForm(true);
    const custom = (product.custom_fields ?? {}) as Record<string, unknown>;
    const customText: Record<string, string> = {};
    for (const field of flexFields) {
      customText[field.field_key] = custom[field.field_key] == null ? "" : String(custom[field.field_key]);
    }
    setFormData({
      name: product.name,
      sku: product.sku ?? "",
      description: product.description ?? "",
      unit_price: product.unit_price != null ? String(product.unit_price) : "",
      currency: product.currency ?? localeCurrency,
      unit_of_measure: product.unit_of_measure ?? "each",
      product_category: product.product_category ?? "",
      product_status: product.product_status ?? (product.is_active === false ? "inactive" : "active"),
      territory_id: product.territory_id ?? territories.find((item) => item.code === "global")?.id ?? "",
      custom_fields: customText,
    });
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(emptyProductForm());
  };

  const save = async () => {
    if (!formData.name.trim() || !formData.sku.trim()) return;
    const priceText = formData.unit_price.trim();
    if (priceText) {
      const price = Number(priceText);
      if (!Number.isFinite(price) || price <= 0) {
        reportSaveBlocked(show, "List price must be a number greater than 0.");
        return;
      }
    }
    const body = {
      name: formData.name,
      sku: formData.sku,
      description: formData.description || null,
      unit_price: formData.unit_price ? Number(formData.unit_price) : null,
      currency: formData.currency || localeCurrency,
      unit_of_measure: formData.unit_of_measure || "each",
      product_category: formData.product_category || null,
      product_status: formData.product_status,
      territory_id: canAssignTerritory ? formData.territory_id || null : null,
      custom_fields: Object.fromEntries(
        Object.entries(formData.custom_fields).filter(([, value]) => value.trim()),
      ),
    };
    try {
      if (editingId) {
        await crmApi.updateProduct(editingId, body, orgId);
      } else {
        await crmApi.createProduct(body, orgId);
      }
      resetForm();
      show("Saved!", "success");
      await load();
    } catch (e) {
      setError(reportSaveFailure(show, e, "Failed to save product"));
    }
  };

  const productStatus = (product: Product) =>
    product.product_status ?? (product.is_active === false ? "inactive" : "active");
  const visibleProducts = statusFilter ? products.filter((p) => productStatus(p) === statusFilter) : products;

  return (
    <ListWorkspace
      title="Products"
      actions={
        <Cluster gap={2}>
          {canConfigureOptions ? (
            <Button type="button" variant="secondary" onClick={() => setSettingsOpen(true)}>
              Product settings
            </Button>
          ) : null}
          <Button
            onClick={() => {
              if (showForm) resetForm();
              else {
                setViewingId(null);
                setEditingId(null);
                setFormData(emptyProductForm());
                setShowForm(true);
              }
            }}
          >
            {showForm ? "Cancel" : "New product"}
          </Button>
        </Cluster>
      }
      toolbar={
        <div className="crm-toolbar">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search products..."
            label="Search products"
            hint="Search by name, SKU, description, category, or status."
          />
          <StatusSlicer
            value={statusFilter}
            onChange={setStatusFilter}
            options={statuses}
            label="Filter by product status"
            optionHints={PRODUCT_STATUS_OPTIONS}
          />
        </div>
      }
    >
      {error && <Card className="crm-error">{error}</Card>}
      {viewing && !showForm ? (
        <Card className="crm-record-view">
          <Stack gap={3}>
            <Cluster gap={2} justify="space-between" align="center">
              <h2 className="crm-section-title">{viewing.name}</h2>
              <Cluster gap={2}>
                <Button size="sm" title={ACTION_HINTS.edit_record} onClick={() => startEdit(viewing)}>Edit</Button>
                <AskLoxleyButton record={{ kind: "product", id: viewing.id, label: viewing.name }} />
                <Button size="sm" variant="ghost" onClick={() => setViewingId(null)}>Close</Button>
              </Cluster>
            </Cluster>
            <DetailSection title="Product details">
              <FieldItem label="Name" hint={FIELD_HINTS.product_name} value={viewing.name} />
              <FieldItem label="SKU / product code" hint={FIELD_HINTS.product_sku} value={viewing.sku || null} />
              {isFieldVisible("unit_price") && (
                <FieldItem
                  label="List price"
                  hint={FIELD_HINTS.product_list_price}
                  value={viewing.unit_price != null ? formatMoney(viewing.unit_price, viewing.currency || localeCurrency) : null}
                />
              )}
              {isFieldVisible("unit_of_measure") && (
                <FieldItem label="Unit of measure" value={statusLabel(viewing.unit_of_measure || "each")} />
              )}
              {isFieldVisible("product_category") && (
                <FieldItem label="Category / family" hint={FIELD_HINTS.product_category}
                  value={viewing.product_category ? statusLabel(viewing.product_category) : null} />
              )}
              {isFieldVisible("territory_id") && (
                <FieldItem label="Territory" value={viewing.territory_name || "Global"} />
              )}
              {isFieldVisible("product_status") && (
                <FieldItem label={statusField?.label ?? "Status"} hint={FIELD_HINTS.product_status}
                  value={statusLabel(productStatus(viewing))} />
              )}
              {flexFields.map((field) => {
                const value = (viewing.custom_fields as Record<string, unknown> | null | undefined)?.[field.field_key];
                return <FieldItem key={field.field_key} label={field.label} value={value == null || value === "" ? null : String(value)} />;
              })}
              {isFieldVisible("description") && viewing.description ? (
                <FieldItem label="Description" hint={FIELD_HINTS.product_description} value={viewing.description} wide />
              ) : null}
            </DetailSection>
            <RecordAuditInfo record={viewing} />
          </Stack>
        </Card>
      ) : null}
      {showForm && (
        <Card>
          <Stack gap={3}>
            <h2 className="crm-section-title">{editingId ? "Edit product" : "New product"}</h2>
            <Input label="Name *" tooltip={FIELD_HINTS.product_name} value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            <Input label="SKU / product code *" tooltip={FIELD_HINTS.product_sku} value={formData.sku} onChange={(e) => setFormData({ ...formData, sku: e.target.value })} />
            {isFieldVisible("description") && (
              <Input label="Description" tooltip={FIELD_HINTS.product_description} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            )}
            {isFieldVisible("unit_price") && (
              <Input label="List price" tooltip={FIELD_HINTS.product_list_price} value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })} />
            )}
            <Select
              label="Currency"
              value={formData.currency || localeCurrency}
              onChange={(event) => setFormData({ ...formData, currency: event.target.value })}
            >
              {Object.keys(CURRENCY_SYMBOLS).map((code) => (
                <option key={code} value={code}>{code}</option>
              ))}
            </Select>
            {isFieldVisible("unit_of_measure") && (
              <Select label="Unit of measure *" value={formData.unit_of_measure} onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}>
                {uoms.map((uom) => (
                  <option key={uom} value={uom}>{statusLabel(uom)}</option>
                ))}
              </Select>
            )}
            {isFieldVisible("product_category") && (
              categories.length > 0 ? (
                <Select label="Category / family *" tooltip={FIELD_HINTS.product_category} value={formData.product_category} onChange={(e) => setFormData({ ...formData, product_category: e.target.value })}>
                  <option value="">Choose a category</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>{statusLabel(category)}</option>
                  ))}
                </Select>
              ) : (
                <Input label="Category / family *" tooltip={FIELD_HINTS.product_category} value={formData.product_category} onChange={(e) => setFormData({ ...formData, product_category: e.target.value })} />
              )
            )}
            {canAssignTerritory && territories.length > 0 && isFieldVisible("territory_id") && (
              <Select
                label="Territory"
                tooltip="Global products are visible in all territories."
                value={formData.territory_id}
                onChange={(e) => setFormData({ ...formData, territory_id: e.target.value })}
              >
                {territories.map((territory) => (
                  <option key={territory.id} value={territory.id}>{territory.name}</option>
                ))}
              </Select>
            )}
            {isFieldVisible("product_status") && (
              <>
                <FieldLabel htmlFor="product-status" hint={FIELD_HINTS.product_status}>
                  {statusField?.label ?? "Status"}
                </FieldLabel>
                <select
                  id="product-status"
                  className="crm-select"
                  value={formData.product_status}
                  onChange={(e) => setFormData({ ...formData, product_status: e.target.value })}
                >
                  {statuses.map((status) => {
                    const meaning = PRODUCT_STATUS_OPTIONS.find(
                      (item) => item.label.toLowerCase() === statusLabel(status).toLowerCase(),
                    )?.meaning;
                    return (
                      <option key={status} value={status} title={meaning}>
                        {statusLabel(status)}
                      </option>
                    );
                  })}
                </select>
              </>
            )}
            {flexFields.map((field) => (
              <Input
                key={field.field_key}
                label={field.label}
                value={formData.custom_fields[field.field_key] ?? ""}
                onChange={(event) =>
                  setFormData({
                    ...formData,
                    custom_fields: { ...formData.custom_fields, [field.field_key]: event.target.value },
                  })
                }
              />
            ))}
            {duplicates && duplicates.matches.length > 0 && !editingId && (
              <p className="crm-warning">A product with this SKU or name already exists. You can still create it.</p>
            )}
            {editingId ? <RecordAuditInfo record={products.find((item) => item.id === editingId) ?? {}} /> : null}
            <div className="crm-form-actions">
              <Button onClick={() => save()} disabled={!formData.name.trim() || !formData.sku.trim()}>
                {editingId ? "Save product" : "Create product"}
              </Button>
              <Button type="button" variant="secondary" onClick={resetForm}>
                Cancel
              </Button>
            </div>
          </Stack>
        </Card>
      )}
      {loading ? (
        <Card>Loading...</Card>
      ) : visibleProducts.length === 0 ? (
        <Card>
          <EmptyState title="No products yet" description="Add the products and services you sell." />
        </Card>
      ) : (
        <Card className="crm-table-card">
          <ResponsiveDataTable
            caption="Products"
            columns={[
              { key: "name", label: "Name", primary: true, hint: FIELD_HINTS.product_name },
              { key: "sku", label: "SKU", primary: true, hint: FIELD_HINTS.product_sku },
              { key: "category", label: "Category", hint: FIELD_HINTS.product_category },
              { key: "uom", label: "UOM", hint: "Unit of measure." },
              { key: "territory", label: "Territory", hint: "Global means every territory can use this product." },
              { key: "price", label: "List price", primary: true, hint: FIELD_HINTS.product_list_price },
              { key: "status", label: "Status", primary: true, hint: FIELD_HINTS.product_status },
            ]}
            rows={visibleProducts.map((p) => ({
              id: p.id,
              onOpen: () => startView(p),
              cells: {
                name: (
                  <>
                    <a
                      href={`#product-${p.id}`}
                      onClick={(event) => {
                        event.preventDefault();
                        startView(p);
                      }}
                    >
                      {p.name}
                    </a>
                    {p.description ? <div className="crm-muted">{p.description}</div> : null}
                  </>
                ),
                sku: p.sku || "-",
                category: p.product_category || "-",
                uom: p.unit_of_measure || "each",
                territory: p.territory_name || "Global",
                price: p.unit_price != null ? formatMoney(p.unit_price, p.currency || localeCurrency) : "-",
                status: (
                  <StatusBadge
                    status={productStatus(p)}
                    options={PRODUCT_STATUS_OPTIONS}
                    className={`crm-badge ${p.is_active === false ? "" : "crm-badge--active"}`}
                  >
                    {statusLabel(productStatus(p))}
                  </StatusBadge>
                ),
              },
              actions: (
                <Cluster gap={2}>
                  <Button variant="ghost" size="sm" aria-label={`View ${p.name}`} onClick={() => startView(p)}
                    title="Open this product to read its details. Nothing can change until you click Edit.">
                    View
                  </Button>
                  <Button variant="secondary" size="sm" title={ACTION_HINTS.edit_record} onClick={() => startEdit(p)}>
                    Edit
                  </Button>
                  <DeleteRecordButton
                    capability="crm.products"
                    recordLabel={p.name}
                    size="sm"
                    variant="danger"
                    onDeleted={async () => {
                      await crmApi.deleteProduct(p.id, orgId);
                      if (viewingId === p.id) setViewingId(null);
                      await load();
                    }}
                  />
                </Cluster>
              ),
            }))}
          />
        </Card>
      )}
      <LoadMore shown={products.length} total={total} hasMore={hasMore} loading={loadingMore} onLoadMore={() => void loadMore()} noun="products" />
      <Drawer isOpen={settingsOpen} onClose={() => setSettingsOpen(false)} title="Product settings">
        <TabFieldOptionsSettings orgId={orgId} entity="product" />
      </Drawer>
    </ListWorkspace>
  );
}
